import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Shape of the brand_config.yaml we care about in the form editor.
// Unknown fields pass through untouched so we never clobber yaml the
// user added by hand.
interface BrandConfig {
  brand?: Record<string, unknown>;
  voice?: {
    pillars?: {
      bold?: string;
      empathetic?: string;
      clear?: string;
      surprising?: string;
    };
    persona?: {
      age?: string;
      description?: string;
    };
    prohibitions?: string[];
  };
  content?: {
    caption_structure?: Record<string, unknown>;
    hashtag_sets?: {
      core?: string[];
      democracy?: string[];
      disinformation?: string[];
      engagement?: string[];
    };
  };
  [k: string]: unknown;
}

interface ContentMix {
  // API actually returns weights as a flat dict at the top level (see
  // config/content_mix.yaml + /api/config/content-mix). Earlier shape
  // assumed `content.categories[k].weight` — that's a different yaml,
  // not this one. Unknown keys (rules, schedule, weekly_rhythm, …) are
  // preserved by spreading `...config` in the save payload.
  weights?: Record<string, number>;
  [k: string]: unknown;
}

async function fetchJson<T>(path: string): Promise<T> {
  const r = await fetch(path);
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}
async function putJson<T>(path: string, data: unknown): Promise<T> {
  const r = await fetch(path, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data }),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export function BrandConfig() {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"form" | "advanced">("form");

  const brand = useQuery({
    queryKey: ["brand_config"],
    queryFn: () => fetchJson<BrandConfig>("/api/config/brand"),
  });
  const mix = useQuery({
    queryKey: ["content_mix"],
    queryFn: () => fetchJson<ContentMix>("/api/config/content-mix"),
  });

  return (
    <div className="space-y-8">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="headline text-6xl text-brand-text-dark">Brand</h1>
          <p className="mt-2 text-sm text-brand-text-dark/60">
            Voice-Pillars, Hashtag-Sets und Content-Mix. Unbekannte YAML-Felder
            bleiben unangetastet — der Form-Editor fasst nur an, was er kennt.
            Kommentare im YAML bleiben erhalten (ruamel.yaml Roundtrip).
          </p>
        </div>
        <div className="inline-flex rounded-full bg-white p-1 shadow-sm ring-1 ring-black/5">
          <ToggleButton
            active={mode === "form"}
            onClick={() => setMode("form")}
          >
            Form
          </ToggleButton>
          <ToggleButton
            active={mode === "advanced"}
            onClick={() => setMode("advanced")}
          >
            Advanced (JSON)
          </ToggleButton>
        </div>
      </div>

      {mode === "form" ? (
        <>
          {brand.data ? (
            <VoicePillarsCard
              config={brand.data}
              onSaved={() =>
                qc.invalidateQueries({ queryKey: ["brand_config"] })
              }
            />
          ) : brand.isLoading ? (
            <SkeletonCard />
          ) : null}
          {brand.data ? (
            <HashtagSetsCard
              config={brand.data}
              onSaved={() =>
                qc.invalidateQueries({ queryKey: ["brand_config"] })
              }
            />
          ) : null}
          {mix.data ? (
            <ContentMixCard
              config={mix.data}
              onSaved={() => qc.invalidateQueries({ queryKey: ["content_mix"] })}
            />
          ) : mix.isLoading ? (
            <SkeletonCard />
          ) : null}
        </>
      ) : (
        <AdvancedJsonEditors />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Voice pillars card
// ─────────────────────────────────────────────────────────────

function VoicePillarsCard({
  config,
  onSaved,
}: {
  config: BrandConfig;
  onSaved: () => void;
}) {
  const pillars = config.voice?.pillars ?? {};
  const persona = config.voice?.persona ?? {};

  const [bold, setBold] = useState(pillars.bold ?? "");
  const [empathetic, setEmpathetic] = useState(pillars.empathetic ?? "");
  const [clear, setClear] = useState(pillars.clear ?? "");
  const [surprising, setSurprising] = useState(pillars.surprising ?? "");
  const [personaAge, setPersonaAge] = useState(persona.age ?? "");
  const [personaDesc, setPersonaDesc] = useState(persona.description ?? "");

  const save = useMutation({
    mutationFn: async () => {
      const next: BrandConfig = {
        ...config,
        voice: {
          ...(config.voice ?? {}),
          pillars: { bold, empathetic, clear, surprising },
          persona: {
            ...(persona ?? {}),
            age: personaAge,
            description: personaDesc,
          },
        },
      };
      return putJson<BrandConfig>("/api/config/brand", next);
    },
    onSuccess: () => onSaved(),
  });

  return (
    <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
      <SectionHeader
        title="Voice & Persona"
        hint="Die vier Pillars prägen jeden Content-Lauf. Persona beschreibt, wie TRULY klingt."
        saving={save.isPending}
        error={save.error ? String(save.error) : null}
        onSave={() => save.mutate()}
      />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <PillarField label="Bold" value={bold} onChange={setBold} rows={3} />
        <PillarField
          label="Empathetic"
          value={empathetic}
          onChange={setEmpathetic}
          rows={3}
        />
        <PillarField label="Clear" value={clear} onChange={setClear} rows={3} />
        <PillarField
          label="Surprising"
          value={surprising}
          onChange={setSurprising}
          rows={3}
        />
      </div>
      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-[200px_1fr]">
        <PillarField
          label="Persona age"
          value={personaAge}
          onChange={setPersonaAge}
          rows={1}
        />
        <PillarField
          label="Persona description"
          value={personaDesc}
          onChange={setPersonaDesc}
          rows={3}
        />
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Hashtag sets card
// ─────────────────────────────────────────────────────────────

function HashtagSetsCard({
  config,
  onSaved,
}: {
  config: BrandConfig;
  onSaved: () => void;
}) {
  const sets = config.content?.hashtag_sets ?? {};

  const [core, setCore] = useState<string[]>(sets.core ?? []);
  const [democracy, setDemocracy] = useState<string[]>(sets.democracy ?? []);
  const [disinfo, setDisinfo] = useState<string[]>(sets.disinformation ?? []);
  const [engagement, setEngagement] = useState<string[]>(sets.engagement ?? []);

  const save = useMutation({
    mutationFn: async () => {
      const next: BrandConfig = {
        ...config,
        content: {
          ...(config.content ?? {}),
          hashtag_sets: {
            ...(sets ?? {}),
            core,
            democracy,
            disinformation: disinfo,
            engagement,
          },
        },
      };
      return putJson<BrandConfig>("/api/config/brand", next);
    },
    onSuccess: () => onSaved(),
  });

  return (
    <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
      <SectionHeader
        title="Hashtag sets"
        hint="Kern-Tags kommen auf jeden Post. Kategorie-Sets werden je nach Post-Typ ausgewählt."
        saving={save.isPending}
        error={save.error ? String(save.error) : null}
        onSave={() => save.mutate()}
      />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TagListField label="Core (always included)" tags={core} onChange={setCore} />
        <TagListField label="Democracy" tags={democracy} onChange={setDemocracy} />
        <TagListField label="Disinformation" tags={disinfo} onChange={setDisinfo} />
        <TagListField
          label="Engagement"
          tags={engagement}
          onChange={setEngagement}
        />
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Content mix card
// ─────────────────────────────────────────────────────────────

function ContentMixCard({
  config,
  onSaved,
}: {
  config: ContentMix;
  onSaved: () => void;
}) {
  const initial = config.weights ?? {};
  const [weights, setWeights] = useState<Record<string, number>>(() =>
    Object.fromEntries(
      Object.entries(initial).map(([k, v]) => [k, Number(v ?? 0)]),
    ),
  );

  const total = Object.values(weights).reduce((a, b) => a + b, 0);

  const save = useMutation({
    mutationFn: async () => {
      const next: ContentMix = {
        ...config,
        weights: { ...weights },
      };
      return putJson<ContentMix>("/api/config/content-mix", next);
    },
    onSuccess: () => onSaved(),
  });

  return (
    <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
      <SectionHeader
        title="Content mix"
        hint="Wie oft welche Kategorie automatisch generiert wird. Summe sollte 1.0 ergeben."
        saving={save.isPending}
        error={save.error ? String(save.error) : null}
        onSave={() => save.mutate()}
      />
      <div className="space-y-2">
        {Object.keys(weights).map((cat) => {
          const value = weights[cat] ?? 0;
          const pct = Math.round(value * 100);
          return (
            <div key={cat} className="flex items-center gap-3">
              <label className="w-40 font-mono text-xs text-brand-text-dark/70">
                {cat}
              </label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={value}
                onChange={(e) =>
                  setWeights((w) => ({ ...w, [cat]: Number(e.target.value) }))
                }
                className="flex-1 accent-brand-blue"
              />
              <div className="w-14 text-right font-mono text-xs tabular-nums">
                {pct}%
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 text-xs text-brand-text-dark/60">
        Summe:{" "}
        <span
          className={`font-mono font-bold ${
            Math.abs(total - 1) < 0.01 ? "text-emerald-700" : "text-amber-700"
          }`}
        >
          {total.toFixed(2)}
        </span>
        {Math.abs(total - 1) >= 0.01 ? (
          <span className="ml-2">(sollte 1.00 sein)</span>
        ) : null}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Advanced JSON editors (the old UI — kept as fallback)
// ─────────────────────────────────────────────────────────────

function AdvancedJsonEditors() {
  const qc = useQueryClient();
  const brand = useQuery({
    queryKey: ["brand_config"],
    queryFn: () => fetchJson("/api/config/brand"),
  });
  const mix = useQuery({
    queryKey: ["content_mix"],
    queryFn: () => fetchJson("/api/config/content-mix"),
  });

  const [brandText, setBrandText] = useState("");
  const [mixText, setMixText] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (brand.data) setBrandText(JSON.stringify(brand.data, null, 2));
  }, [brand.data]);
  useEffect(() => {
    if (mix.data) setMixText(JSON.stringify(mix.data, null, 2));
  }, [mix.data]);

  const saveBrand = useMutation({
    mutationFn: (parsed: unknown) => putJson("/api/config/brand", parsed),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["brand_config"] });
    },
    onError: (e: Error) => setError(e.message),
  });
  const saveMix = useMutation({
    mutationFn: (parsed: unknown) => putJson("/api/config/content-mix", parsed),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["content_mix"] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const trySave = (text: string, fn: (data: unknown) => void) => {
    try {
      fn(JSON.parse(text));
    } catch (e) {
      setError(`Invalid JSON: ${String(e)}`);
    }
  };

  return (
    <>
      {error ? (
        <div className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-900 ring-1 ring-red-200">
          {error}
        </div>
      ) : null}
      <AdvancedEditor
        title="brand_config.yaml"
        text={brandText}
        onText={setBrandText}
        onSave={() => trySave(brandText, saveBrand.mutate)}
        saving={saveBrand.isPending}
      />
      <AdvancedEditor
        title="content_mix.yaml"
        text={mixText}
        onText={setMixText}
        onSave={() => trySave(mixText, saveMix.mutate)}
        saving={saveMix.isPending}
      />
    </>
  );
}

function AdvancedEditor({
  title,
  text,
  onText,
  onSave,
  saving,
}: {
  title: string;
  text: string;
  onText: (s: string) => void;
  onSave: () => void;
  saving: boolean;
}) {
  return (
    <section className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-black/5">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-wider">{title}</h2>
        <button
          onClick={onSave}
          disabled={saving}
          className="rounded-full bg-brand-blue px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-brand-cream hover:bg-brand-blue-deep disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      <textarea
        value={text}
        onChange={(e) => onText(e.target.value)}
        rows={20}
        className="w-full resize-y rounded-lg border border-black/10 bg-brand-text-dark p-4 font-mono text-xs text-brand-cream focus:border-brand-blue focus:outline-none"
        spellCheck={false}
      />
      <p className="mt-2 text-[11px] text-brand-text-dark/50">
        Input ist JSON (UI-bequem); der Server schreibt es als YAML zurück mit erhaltenen Kommentaren.
      </p>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Small components
// ─────────────────────────────────────────────────────────────

function SectionHeader({
  title,
  hint,
  saving,
  error,
  onSave,
}: {
  title: string;
  hint: string;
  saving: boolean;
  error: string | null;
  onSave: () => void;
}) {
  return (
    <div className="mb-4 flex items-start justify-between gap-4">
      <div>
        <h2 className="text-sm font-bold uppercase tracking-wider text-brand-text-dark">
          {title}
        </h2>
        <p className="mt-1 text-xs text-brand-text-dark/60">{hint}</p>
        {error ? (
          <p className="mt-2 rounded-md bg-red-50 px-2 py-1 text-xs text-red-900">
            {error}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="rounded-full bg-brand-blue px-4 py-2 text-xs font-bold uppercase tracking-wider text-brand-cream hover:bg-brand-blue-deep disabled:opacity-50"
      >
        {saving ? "…" : "Speichern"}
      </button>
    </div>
  );
}

function PillarField({
  label,
  value,
  onChange,
  rows = 2,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <label className="block">
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-brand-text-dark/60">
        {label}
      </div>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full resize-y rounded-lg border border-black/10 bg-white px-3 py-2 text-sm focus:border-brand-blue focus:outline-none"
      />
    </label>
  );
}

function TagListField({
  label,
  tags,
  onChange,
}: {
  label: string;
  tags: string[];
  onChange: (next: string[]) => void;
}) {
  const [input, setInput] = useState("");
  return (
    <div>
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-brand-text-dark/60">
        {label}
      </div>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {tags.map((t) => (
          <span
            key={t}
            className="flex items-center gap-1 rounded-md bg-brand-beige-deep px-2.5 py-1 font-mono text-[11px] text-brand-text-dark"
          >
            {t}
            <button
              type="button"
              onClick={() => onChange(tags.filter((x) => x !== t))}
              className="text-brand-text-dark/40 hover:text-red-600"
              aria-label={`Remove ${t}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={input}
        placeholder="#tag hinzufügen + Enter"
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            const raw = input.trim().replace(/^#?/, "#");
            if (raw.length >= 2 && !tags.includes(raw)) {
              onChange([...tags, raw]);
            }
            setInput("");
          } else if (e.key === "Backspace" && input === "" && tags.length) {
            onChange(tags.slice(0, -1));
          }
        }}
        className="w-full rounded-lg border border-black/10 bg-white px-3 py-1.5 text-sm focus:border-brand-blue focus:outline-none"
      />
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition ${
        active
          ? "bg-brand-text-dark text-brand-cream"
          : "text-brand-text-dark/60 hover:text-brand-text-dark"
      }`}
    >
      {children}
    </button>
  );
}

function SkeletonCard() {
  return (
    <div className="h-48 animate-pulse rounded-2xl bg-white/50 ring-1 ring-black/5" />
  );
}
